import type { ButtonHTMLAttributes } from 'react';

// Sleek's flat buttons (partner mockup): white primaries, dark secondaries.
// 44px with 12px corners across the 2026-10-06 Figma.
const FLAT =
  'flex min-w-0 items-center justify-center h-11 px-4 ' +
  'whitespace-nowrap text-[16px] font-semibold tracking-[-0.31px] ' +
  'cursor-pointer transition-transform active:scale-[0.98] ' +
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100 ';

// Round 40px buttons — the reveal's X and the back arrow on What's inside and
// the Vault (partner Figma 2026-10-06: flat, no glass).
const ROUND_FLAT =
  'relative inline-flex h-10 w-10 items-center justify-center border border-solid ' +
  'cursor-pointer select-none transition-transform active:scale-[0.97] ';

// Rounded 40px pill under the lobby's pack title ("What's inside").
const PILL =
  'inline-flex items-center justify-center gap-1.5 h-10 px-3 border border-solid ' +
  'whitespace-nowrap text-[14px] leading-[20px] font-semibold tracking-[-0.15px] ' +
  'cursor-pointer select-none transition-transform active:scale-[0.97] ' +
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100 ';

// One entry per button so the Design Constructor themes each on its own
// (Slava: "отдельно каждая"). The var names stay literal — Tailwind and the
// registry invariant both read them from source, so no template strings.
const SKINS = {
  RevealCloseButton:
    ROUND_FLAT +
    '[border-radius:var(--co-RevealCloseButton-borderRadius,60px)] ' +
    '[background:var(--co-RevealCloseButton-fill,#1A1B1F)] ' +
    '[border-color:var(--co-RevealCloseButton-border,rgba(255,255,255,0.01))] ' +
    '[color:var(--co-RevealCloseButton-icon,#FFFFFF)]',
  RevealRipButton:
    FLAT +
    '[border-radius:var(--co-RevealRipButton-borderRadius,12px)] ' +
    'bg-[var(--co-RevealRipButton-fill,#FFFFFF)] text-[var(--co-RevealRipButton-text,#101114)]',
  RevealVaultButton:
    FLAT +
    '[border-radius:var(--co-RevealVaultButton-borderRadius,12px)] ' +
    'bg-[var(--co-RevealVaultButton-fill,#1A1B1F)] text-[var(--co-RevealVaultButton-text,#FFFFFF)]',
  RevealSellButton:
    FLAT +
    '[border-radius:var(--co-RevealSellButton-borderRadius,12px)] ' +
    'bg-[var(--co-RevealSellButton-fill,#FFFFFF)] text-[var(--co-RevealSellButton-text,#101114)]',
  RevealNextPackButton:
    FLAT +
    '[border-radius:var(--co-RevealNextPackButton-borderRadius,12px)] ' +
    'bg-[var(--co-RevealNextPackButton-fill,#FFFFFF)] text-[var(--co-RevealNextPackButton-text,#101114)]',
  RevealDoneButton:
    FLAT +
    '[border-radius:var(--co-RevealDoneButton-borderRadius,12px)] ' +
    'bg-[var(--co-RevealDoneButton-fill,#1A1B1F)] text-[var(--co-RevealDoneButton-text,#FFFFFF)]',
  RevealCloseAndVaultButton:
    FLAT +
    '[border-radius:var(--co-RevealCloseAndVaultButton-borderRadius,12px)] ' +
    'bg-[var(--co-RevealCloseAndVaultButton-fill,#FFFFFF)] text-[var(--co-RevealCloseAndVaultButton-text,#101114)]',
  RevealContinueRippingButton:
    FLAT +
    '[border-radius:var(--co-RevealContinueRippingButton-borderRadius,12px)] ' +
    'bg-[var(--co-RevealContinueRippingButton-fill,#1A1B1F)] text-[var(--co-RevealContinueRippingButton-text,#FFFFFF)]',
  // The empty Vault's "Buy packs" — a 44px pill (partner Figma 2026-10-14).
  VaultEmptyStateButton:
    'inline-flex items-center justify-center h-11 px-5 ' +
    'whitespace-nowrap text-[15px] leading-[24px] font-semibold tracking-[-0.23px] ' +
    'cursor-pointer transition-transform active:scale-[0.98] ' +
    '[border-radius:var(--co-VaultEmptyStateButton-borderRadius,100px)] ' +
    'bg-[var(--co-VaultEmptyStateButton-fill,#F4F4F5)] text-[var(--co-VaultEmptyStateButton-text,#0E0F11)]',
  MyPacksSellNowButton:
    FLAT +
    '[border-radius:var(--co-MyPacksSellNowButton-borderRadius,8px)] ' +
    'bg-[var(--co-MyPacksSellNowButton-fill,#F4F4F5)] text-[var(--co-MyPacksSellNowButton-text,#101114)]',
  MyPacksShipButton:
    FLAT +
    '[border-radius:var(--co-MyPacksShipButton-borderRadius,12px)] ' +
    'bg-[var(--co-MyPacksShipButton-fill,#1A1B1F)] text-[var(--co-MyPacksShipButton-text,#FFFFFF)]',
  MyPacksSellButton:
    FLAT +
    '[border-radius:var(--co-MyPacksSellButton-borderRadius,12px)] ' +
    'bg-[var(--co-MyPacksSellButton-fill,#FFFFFF)] text-[var(--co-MyPacksSellButton-text,#101114)]',
  ShippingPrimaryButton:
    FLAT +
    '[border-radius:var(--co-ShippingPrimaryButton-borderRadius,12px)] ' +
    'bg-[var(--co-ShippingPrimaryButton-fill,#FFFFFF)] text-[var(--co-ShippingPrimaryButton-text,#101114)] ' +
    // Disabled reads as a flat dark slab, not a faded white one (partner
    // Figma 2026-10-08, "Confirm shipment" / "Confirm address").
    'disabled:opacity-100 disabled:bg-[var(--co-ShippingPrimaryButton-disabledFill,#1A1B1F)] ' +
    'disabled:text-[var(--co-ShippingPrimaryButton-disabledText,#43444F)]',
  ShipAddAddressButton:
    'flex w-full items-center justify-center h-10 px-4 ' +
    'whitespace-nowrap text-[14px] leading-[20px] font-semibold tracking-[-0.15px] ' +
    'cursor-pointer transition-transform active:scale-[0.98] ' +
    '[border-radius:var(--co-ShipAddAddressButton-borderRadius,10px)] ' +
    'bg-[var(--co-ShipAddAddressButton-fill,#9333EA)] text-[var(--co-ShipAddAddressButton-text,#FFFFFF)]',
  ShippingSecondaryButton:
    FLAT +
    '[border-radius:var(--co-ShippingSecondaryButton-borderRadius,12px)] ' +
    'bg-[var(--co-ShippingSecondaryButton-fill,#25262C)] text-[var(--co-ShippingSecondaryButton-text,#FFFFFF)]',
  WhatsInsideButton:
    PILL +
    '[border-radius:var(--co-WhatsInsideButton-borderRadius,60px)] ' +
    '[border-color:var(--co-WhatsInsideButton-border,rgba(255,255,255,0.01))] ' +
    'bg-[var(--co-WhatsInsideButton-fill,#1A1B1F)] text-[var(--co-WhatsInsideButton-text,#FFFFFF)]',
  SleekBackButton:
    ROUND_FLAT +
    '[border-radius:var(--co-SleekBackButton-borderRadius,60px)] ' +
    '[border-color:var(--co-SleekBackButton-border,rgba(255,255,255,0.01))] ' +
    '[background:var(--co-SleekBackButton-fill,#1A1B1F)] ' +
    '[color:var(--co-SleekBackButton-icon,#FFFFFF)]',
  WhatsInsideCloseButton:
    FLAT +
    '[border-radius:var(--co-WhatsInsideCloseButton-borderRadius,12px)] ' +
    'bg-[var(--co-WhatsInsideCloseButton-fill,#1A1B1F)] text-[var(--co-WhatsInsideCloseButton-text,#FFFFFF)]',
  // Provably fair (partner Figma 2026-10-08): the odds sheet's "Learn More"
  // pill and the fairness sheet's round X / outlined DONE. The pill has its
  // own base: PILL's 40px / 14px would clash with these utilities and leave
  // the winner to the stylesheet order.
  FairnessLearnMoreButton:
    'inline-flex items-center justify-center h-11 px-5 border border-solid ' +
    'whitespace-nowrap text-[16px] font-semibold tracking-[-0.31px] ' +
    'cursor-pointer select-none transition-transform active:scale-[0.97] ' +
    '[border-radius:var(--co-FairnessLearnMoreButton-borderRadius,60px)] ' +
    '[border-color:var(--co-FairnessLearnMoreButton-border,rgba(255,255,255,0.16))] ' +
    'bg-[var(--co-FairnessLearnMoreButton-fill,#1A1B1F)] text-[var(--co-FairnessLearnMoreButton-text,#FFFFFF)]',
  FairnessCloseButton:
    ROUND_FLAT +
    '[border-radius:var(--co-FairnessCloseButton-borderRadius,60px)] ' +
    '[border-color:var(--co-FairnessCloseButton-border,rgba(255,255,255,0.01))] ' +
    '[background:var(--co-FairnessCloseButton-fill,#25262C)] ' +
    '[color:var(--co-FairnessCloseButton-icon,#FFFFFF)]',
  FairnessDoneButton:
    FLAT +
    'w-full border border-solid uppercase ' +
    '[border-radius:var(--co-FairnessDoneButton-borderRadius,60px)] ' +
    '[border-color:var(--co-FairnessDoneButton-border,rgba(255,255,255,0.16))] ' +
    'bg-[var(--co-FairnessDoneButton-fill,#1A1B1F)] text-[var(--co-FairnessDoneButton-text,#FFFFFF)]',
} as const;

export type SleekButtonName = keyof typeof SKINS;

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Registry component this button is themed and inspected as. */
  name: SleekButtonName;
};

/**
 * A sleek button. `name` picks both its skin and its `data-co-component`,
 * so a click in the Constructor preview opens exactly the knobs it reads.
 */
export function SleekButton({ name, className = '', type = 'button', ...rest }: Props) {
  return <button type={type} data-co-component={name} className={`${SKINS[name]} ${className}`.trim()} {...rest} />;
}
